import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Row from '@amzn/meridian/row';
import Link from '@amzn/meridian/link';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Icon from '@amzn/meridian/icon';
import Tooltip from '@amzn/meridian/tooltip';
import bulbTokens from '@amzn/meridian-tokens/base/icon/bulb';
import { PlanEntryInput, ValidateMacroplanogramCapacityInput } from '@graphql/generated/graphql';
import useValidateMacroplanogramCapacity from '@store/hooks/useValidateMacroplanogramCapacity';

/**
 * The assortment change to validate, expressed as deltas against the current
 * macroplanogram snapshot. This is intentionally flow-agnostic: bulk upload
 * derives these from a plan diff, Add Items passes its staged items as
 * `itemsToAdd`, etc.
 */
export interface CapacityValidationTarget {
  storeId: string;
  macroplanogramId: string;
  /** Snapshot time the capacity is evaluated at (swap/launch time). */
  startTime: number;
  itemsToAdd: PlanEntryInput[];
  itemsToDelete: PlanEntryInput[];
}

export interface CapacityValidationBannerProps {
  /** Current target deltas. When these change, the re-check link is enabled. */
  target: CapacityValidationTarget;
}

const toInput = (t: CapacityValidationTarget): ValidateMacroplanogramCapacityInput => ({
  storeId: t.storeId,
  macroplanogramId: t.macroplanogramId,
  startTime: t.startTime,
  itemsToAdd: t.itemsToAdd,
  itemsToDelete: t.itemsToDelete,
});

const sameDelta = (a: PlanEntryInput[] = [], b: PlanEntryInput[] = []): boolean => {
  if (a.length !== b.length) return false;
  const key = (e: PlanEntryInput) => `${e.itemType}:${e.itemId}`;
  const keysA = new Set(a.map(key));
  return b.every((e) => keysA.has(key(e)));
};

const sameTarget = (a: ValidateMacroplanogramCapacityInput, b: ValidateMacroplanogramCapacityInput): boolean =>
  a.storeId === b.storeId &&
  a.macroplanogramId === b.macroplanogramId &&
  a.startTime === b.startTime &&
  sameDelta(a.itemsToAdd ?? [], b.itemsToAdd ?? []) &&
  sameDelta(a.itemsToDelete ?? [], b.itemsToDelete ?? []);

/**
 * Macroplanogram capacity validation banner. Reusable across any flow that can
 * express an assortment change as add/delete deltas (bulk upload, add items,
 * etc.). It runs an initial validation against the target it mounts with, then
 * exposes a "Re-check capacity" link that re-validates the current target on
 * demand. Edits never auto-trigger a call.
 *
 * Feature gating (GFC-only) is the caller's responsibility: only render this
 * component when the capacityValidation feature is enabled. The validation
 * query fires exactly when this UI is shown.
 */
const CapacityValidationBanner = ({ target }: CapacityValidationBannerProps) => {
  // Holds the target of the last validation. Seeded from the mount target; the
  // re-check link re-points it at the current target.
  const [validatedInput, setValidatedInput] = useState<ValidateMacroplanogramCapacityInput>(() => toInput(target));

  const {
    capacityValidation,
    isFetching: isCapacityFetching,
    isSuccess: isCapacitySuccess,
    isError: isCapacityError,
  } = useValidateMacroplanogramCapacity(validatedInput);

  // First-paint gate: show a loader until the initial validation settles so the
  // banner area is reserved and doesn't pop in. Re-checks don't re-gate.
  const [hasSettledOnce, setHasSettledOnce] = useState(false);
  useEffect(() => {
    if (isCapacitySuccess || isCapacityError) {
      setHasSettledOnce(true);
    }
  }, [isCapacitySuccess, isCapacityError]);

  const currentInput = useMemo(() => toInput(target), [target]);

  // Enable the re-check link only when the current target differs from what was
  // last validated.
  const hasUnvalidatedChanges = !sameTarget(currentInput, validatedInput);

  const handleRecheckCapacity = useCallback(() => {
    setValidatedInput(currentInput);
  }, [currentInput]);

  if (!hasSettledOnce) {
    return (
      <Row spacing="200" alignmentVertical="center">
        <Loader type="circular" size="small" />
        <Text type="b200">Validating capacity…</Text>
      </Row>
    );
  }

  const total = capacityValidation?.totalMacroplanogramVolumeInCubicMillimeters;
  const projected = capacityValidation?.projectedOccupiedVolumeInCubicMillimeters;
  const isCapacityUnavailable =
    !capacityValidation || !!capacityValidation.error || total == null || projected == null || total === 0;
  const isOverCapacity = !isCapacityUnavailable && projected! > total!;
  const utilizationPct = !isCapacityUnavailable ? Math.round((projected! / total!) * 100) : 0;

  // Conservative display in liters (1 L = 1e6 mm³): round projected (occupied)
  // UP and total (available) DOWN to 2 decimals, so the utilization shown never
  // understates how full the fixture is. Text is built as a single string so it
  // renders as one contiguous node (readable for screen readers and testable).
  const projectedL = !isCapacityUnavailable ? (Math.ceil(projected! / 1e4) / 100).toFixed(2) : '';
  const totalL = !isCapacityUnavailable ? (Math.floor(total! / 1e4) / 100).toFixed(2) : '';
  const overCapacityText = `Projected capacity exceeds available fixture volume. Projected: ${projectedL}L / Available: ${totalL}L (${utilizationPct}% utilized). You may still save, but the assortment may not physically fit.`;
  const withinLimitsText = `Capacity within limits. Projected: ${projectedL}L / Available: ${totalL}L (${utilizationPct}% utilized).`;

  const recheckLink = (
    <Row spacing="200" alignmentVertical="center">
      <Link onClick={handleRecheckCapacity} disabled={!hasUnvalidatedChanges || isCapacityFetching}>
        {isCapacityFetching ? 'Re-checking capacity…' : 'Re-check capacity'}
      </Link>
      {isCapacityFetching && <Loader type="circular" size="small" />}
    </Row>
  );

  // The shown result reflects the last validated target. When the current
  // target has drifted (table edited) and we're not already re-checking, the
  // result is stale — dim just the message to signal the numbers no longer
  // match the edits, while keeping the re-check link fully legible/clickable.
  const isStale = hasUnvalidatedChanges && !isCapacityFetching;
  const messageStyle: React.CSSProperties | undefined = isStale ? { opacity: 0.5 } : undefined;

  if (isCapacityUnavailable) {
    return (
      <Alert type="warning" size="small">
        <Row spacing="200" alignmentVertical="center">
          <span style={messageStyle}>
            Capacity validation unavailable: missing casepack / fixture / floorplan data.
          </span>
          {capacityValidation?.error && (
            <Tooltip position="end" title={capacityValidation.error.errorMessage}>
              <Icon tokens={bulbTokens} />
            </Tooltip>
          )}
          {recheckLink}
        </Row>
      </Alert>
    );
  }

  if (isOverCapacity) {
    return (
      <Alert type="error" size="small">
        <Row spacing="200" alignmentVertical="center">
          <span style={messageStyle}>{overCapacityText}</span>
          {recheckLink}
        </Row>
      </Alert>
    );
  }

  return (
    <Alert type="success" size="small">
      <Row spacing="200" alignmentVertical="center">
        <span style={messageStyle}>{withinLimitsText}</span>
        {recheckLink}
      </Row>
    </Alert>
  );
};

export default CapacityValidationBanner;
